import React, { forwardRef, useEffect, useRef, useState } from 'react';
import clsx from 'clsx';
import { Element } from '../element';
import { Icon } from '../icon';
import { setRef, useScrollDirection } from '../../../hooks';
import * as styles from './scrollable.styles';

/** @type {typeof import(".").Scrollable} */
const Scrollable = forwardRef(
  (
    { className, children, step = 100, snap, arrows = 'auto', direction = 'horizontal', onManualScroll, scrollRef, iconOffset, style, ...props },
    ref,
  ) => {
    const innerScrollRef = useRef(null);
    const isHorizontal = direction === 'horizontal';
    const { left, right, top, bottom } = useScrollDirection({ ref: innerScrollRef });
    const showArrow = isHorizontal && arrows !== 'hidden' && ((arrows === 'auto' && (left || right)) || arrows === 'always');
    const [visible, setVisible] = useState(false);
    useEffect(() => {
      setVisible(true);
    }, []);
    return (
      <Element
        as="div"
        ref={ref}
        className={clsx(styles.scrollable, className, {
          [styles.variants[direction]]: direction,
          [styles.variants.visible]: visible,
        })}
        style={{ ...style, ...(iconOffset && { '--scrollable-icon-offset': iconOffset }) }}
        {...props}
      >
        {showArrow && (
          <Icon
            name="chevron-left"
            data-scroll-role="icon-left"
            data-scroll={left}
            className={clsx(styles.components.scrollableIcon, 'left', { [styles.variants.show]: left })}
            onClick={(evt) => {
              innerScrollRef.current.scrollLeft -= snap ? innerScrollRef.current.getBoundingClientRect().width : step;
              onManualScroll?.(evt);
            }}
          />
        )}
        <Element as="div" ref={setRef(innerScrollRef, scrollRef)} className={clsx(styles.components.content)}>
          <div
            className={clsx(styles.components.fade, {
              [styles.variants[direction]]: direction,
              [styles.variants.left]: isHorizontal,
              [styles.variants.top]: !isHorizontal,
              [styles.variants.show]: (isHorizontal && left) || (!isHorizontal && top),
            })}
          />
          {children}
          <div
            className={clsx(styles.components.fade, {
              [styles.variants[direction]]: direction,
              [styles.variants.right]: isHorizontal,
              [styles.variants.bottom]: !isHorizontal,
              [styles.variants.show]: (isHorizontal && right) || (!isHorizontal && bottom),
            })}
          />
        </Element>

        {showArrow && (
          <Icon
            name="chevron-right"
            className={clsx(styles.components.scrollableIcon, 'right', { [styles.variants.show]: right })}
            data-scroll-role="icon-right"
            data-scroll={right}
            onClick={(evt) => {
              innerScrollRef.current.scrollLeft += snap ? innerScrollRef.current.getBoundingClientRect().width : step;
              onManualScroll?.(evt);
            }}
          />
        )}
      </Element>
    );
  },
);

if (process.env.NODE_ENV !== 'production') {
  Scrollable.displayName = 'Scrollable';
}

export default Scrollable;
